import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/chapter_list   { page, limit, search?, sortyBy?, sortDirection? }
//                             → { success, data: { docs, totalDocs, page, limit, totalPages, ... }, message }
//
//   POST /admin/chapter_get    { id }
//                             → { success, data: <chapter>, message }
//
//   POST /admin/chapter_create { album_id?, album_ids[], title, author,
//                                S3_cover_image, cover_image_name,
//                                description, s3_data, s30_data, file_name,
//                                duration, chapter_type,
//                                is_notification, notification_message, order }
//                             → { success, data, message }
//
//   POST /admin/chapter_update { id, ...same as create EXCEPT no s3_data /
//                                s30_data / file_name / duration / is_notification /
//                                notification_message — those are CREATE-only
//                                in the legacy form }
//                             → { success, data, message }
//
//   POST /admin/chapter_delete { id }
//                             → { success, message }
//
// NOTE 1: backend expects 'sortyBy' typo (extra "y") on list. Preserved.
// NOTE 2: capital 'S' in 'S3_cover_image' preserved (same as albums).
// NOTE 3: chapter_type sent as number (1=Free, 2=Paid). List endpoint may
//         return it as string; render code handles both.
// ───────────────────────────────────────────────────────────────────────

export type AdminChapter = {
  _id: string
  title?: string
  description?: string
  author?: string
  chapter_type?: number | string
  album_id?: { _id: string; title: string } | string | null
  album_ids?: string[]
  cover_image?: string
  cover_image_name?: string
  S3_cover_image?: unknown
  video_url?: string
  thumb_url?: string
  thumbnail?: string
  Key?: string
  thumbkey?: string
  shareUrl?: string
  shareUrlLocation?: string
  duration?: number | string
  durationInSec?: number
  is_notification?: boolean
  notification_message?: string
  order?: number
  /** V-Q5: "Show in carousel" — controls Discover "New Releases" rail
   *  visibility. Defaults true on the schema; toggle off to hide. */
  is_carousel?: boolean
  carousel_order?: number
  like_count?: number
  share_count?: number
  comment_count?: number
  /** V-Q5 metadata fields surfaced on ContentItem.metadata. */
  producer?: string
  speaker?: string
  curator?: string
  publicly_available?: boolean
  /** V-Q10 admin soft-hide toggle. True = filtered out of every
   *  user-facing surface; still visible in admin. */
  is_hidden?: boolean
  created_at?: string
  updated_at?: string
}

export type ChapterListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type ChapterListResponse = {
  docs: AdminChapter[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

const chaptersListKey = (p: ChapterListParams) =>
  ["admin", "chapters", "list", p] as const

export function useChaptersList(params: ChapterListParams) {
  return useQuery({
    queryKey: chaptersListKey(params),
    queryFn: async (): Promise<ChapterListResponse> => {
      const res = await api.post<{
        success: boolean
        data: ChapterListResponse
        message: string
      }>("/admin/chapter_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useGetChapter(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "chapters", "get", id] as const,
    queryFn: async (): Promise<AdminChapter | null> => {
      if (!id) return null
      const res = await api.post<{
        success: boolean
        data: AdminChapter
        message: string
      }>("/admin/chapter_get", { id })
      return res.data?.data ?? null
    },
    enabled: !!id,
  })
}

// CREATE payload — full set (includes the upload + duration + notification
// fields that legacy hides on edit).
type ChapterCreatePayload = {
  album_id?: string
  album_ids: string[]
  title: string
  author: string
  S3_cover_image?: unknown
  cover_image_name?: string
  description: string
  s3_data?: unknown
  s30_data?: unknown
  file_name?: string
  duration?: number | string
  chapter_type: number
  is_notification: boolean
  notification_message: string
  order: number
  /** V-Q5 carousel toggle. */
  is_carousel?: boolean
  carousel_order?: number
  /** V-Q5 metadata. Empty string clears; absent leaves the existing value. */
  producer?: string
  speaker?: string
  curator?: string
  publicly_available?: boolean
  /** V-Q10 admin soft-hide toggle. */
  is_hidden?: boolean
}

// UPDATE payload — narrower; legacy hides video upload + duration +
// notification UI on edit. The save handler still builds the full APIData
// object with the omitted fields as undefined; we explicitly use the narrower
// type so TS catches accidental sends of CREATE-only fields.
type ChapterUpdatePayload = {
  id: string
  album_id?: string
  album_ids: string[]
  title: string
  author: string
  S3_cover_image?: unknown
  cover_image_name?: string
  description: string
  chapter_type: number
  order: number
  // Legacy DOES re-send is_notification + notification_message on update
  // even though the UI is hidden — preserved.
  is_notification?: boolean
  notification_message?: string
  /** V-Q5 carousel toggle. */
  is_carousel?: boolean
  carousel_order?: number
  /** V-Q5: optional audio replacement on edit. Send all four together
   *  when a new file was uploaded; omit to keep the existing media. */
  s3_data?: unknown
  s30_data?: unknown
  file_name?: string
  duration?: number | string
  /** V-Q5 metadata, same opt-in pattern as create. */
  producer?: string
  speaker?: string
  curator?: string
  publicly_available?: boolean
  /** V-Q10 admin soft-hide toggle. */
  is_hidden?: boolean
}

export function useCreateChapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ChapterCreatePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminChapter
        message: string
      }>("/admin/chapter_create", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "chapters"] })
    },
  })
}

export function useUpdateChapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: ChapterUpdatePayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminChapter
        message: string
      }>("/admin/chapter_update", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "chapters"] })
    },
  })
}

export function useDeleteChapter() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/chapter_delete", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "chapters"] })
    },
  })
}
